import {IBookingHistoryService} from "./booking-history.service.interface";
import {ServiceBase} from "../service-base";
import {IServiceFactory} from "../service-factory.interface";
import {AnonymousUserBookingHistoryStrategy} from "./strategies/anonymous/anonymous-user-booking-history.strategy";
import {makeObservable, observable, reaction} from "mobx";
import {IJourneyHistoryViewModel} from "./models/journey-history-view-model.interface";
import {IPassengerSegmentBoardingPassViewModel} from "../booking/boarding-pass/passenger-segment-boarding-pass-view-model.interface";
import {IBookingHistoryStrategy} from "./strategies/booking-history-strategy.interface";
import {AuthorizedB2cUserBookingHistoryStrategy} from "./strategies/authorized/authorized-b2c-user-booking-history.strategy";
import {BookingModel} from "../booking/models/booking.model";
import {VoidBookingHistoryStrategy} from "./strategies/void-strategy/void-booking-history.strategy";
import {ISearchBookingByEmailParams, ISearchBookingByLastNameParams} from "../booking/booking.service.interface";
import {IBookingHistoryViewModel} from "./models/booking-history-view-model.interface";
import {IBookingViewModel} from "../booking/models/booking-view-model.interface";

export class BookingHistoryService extends ServiceBase implements IBookingHistoryService {
    constructor(services: IServiceFactory) {
        super(services);

        this._currentStrategy = new VoidBookingHistoryStrategy();

        makeObservable<this, '_currentStrategy'>(this, {
            _currentStrategy: observable.ref
        });

        reaction(() => {
                return {
                    isAuthorized: this.services.user.isAuthorized,
                    shouldLoadBookingHistoryOnStartup: this.services.user.profile.shouldLoadBookingHistoryOnStartup
                }
            },
            (reactionData) => {
                if(reactionData.shouldLoadBookingHistoryOnStartup && !this.services.layout.shouldUseWebappLayout) {
                    this._currentStrategy.dispose();
                    if (reactionData.isAuthorized) {
                        this._currentStrategy = new AuthorizedB2cUserBookingHistoryStrategy(this.services);
                    } else {
                        this._currentStrategy = new AnonymousUserBookingHistoryStrategy(this.services);
                    }
                }
            }, {
                fireImmediately: true
            });

        reaction(() => this.services.booking.isThereABookingInProgress,
            async (isBookingInProgress: boolean) => {
                await this.refreshBookingHistoryForCurrentActiveBooking();
                if(isBookingInProgress) {
                    this._currentActiveBooking = this.services.booking.current;
                } else {
                    this._currentActiveBooking = null;
                }
        }, {
            fireImmediately: true
        })
    }

    private _currentStrategy: IBookingHistoryStrategy;
    private _currentActiveBooking: IBookingViewModel | null = null;

    async refreshBookingHistoryForCurrentActiveBooking(): Promise<void> {
        if(!this._currentActiveBooking?.recordLocator) {
            return;
        }

        await this._currentStrategy.refreshBooking(this._currentActiveBooking.recordLocator);

    }

    get isLoadingHistoryInProgress(): boolean {
        return this._currentStrategy.isLoadingHistoryInProgress;
    }

    get myFutureFlights(): IJourneyHistoryViewModel[] {
        return this._currentStrategy.myFutureFlights;
    }

    get myPastFlights(): IJourneyHistoryViewModel[] {
        return this._currentStrategy.myPastFlights;
    }

    getSavedBoardingPasses(tripReferenceKey: string): IPassengerSegmentBoardingPassViewModel[] {
        return this._currentStrategy.getSavedBoardingPasses(tripReferenceKey);
    }

    retrieveBookingToHistoryByEmail(findByEmailRequest: ISearchBookingByEmailParams): Promise<IBookingHistoryViewModel | null> {
        return this._currentStrategy.retrieveBookingToHistoryByEmail(findByEmailRequest);
    }

    retrieveBookingToHistoryByLastName(findByLastNameRequest: ISearchBookingByLastNameParams): Promise<IBookingHistoryViewModel | null> {
        return this._currentStrategy.retrieveBookingToHistoryByLastName(findByLastNameRequest);
    }

    saveBoardingPasses(boardingPasses: IPassengerSegmentBoardingPassViewModel[]): IPassengerSegmentBoardingPassViewModel[] {
        return this._currentStrategy.saveBoardingPasses(boardingPasses);
    }

    saveToMyTrips(bookingModel: BookingModel): void {
        this._currentStrategy.saveToMyTrips(bookingModel);
    }

    getNextFlights(): IJourneyHistoryViewModel[] {
        return this._currentStrategy.getNextFlights();
    }

    removeBooking(recordLocator: string): void {
        return this._currentStrategy.removeBooking(recordLocator)
    }
}
